import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import SectionHeading from "../components/SectionHeading";
import Button from "./ui/Button";
import EventCardsBlock from "./EventCardsBlock";
import { HeadingVariant } from "../types/constants";
import { ButtonVariant } from "../types/constants";
import { eventsServiceFactory } from "../services/EventsServiceImpl";
import { IEventFlat } from "../domain/models/Events";
import { environment } from "../environments/environment";

const eventService = eventsServiceFactory();

export default function Events() {
    const { t, i18n } = useTranslation();

    const maxEventsCountValue = Number.parseInt(String(environment.EVENTS_MAX_COUNT ?? ""), 10);
    const maxEventsCount = Number.isFinite(maxEventsCountValue) && maxEventsCountValue > 0 ? maxEventsCountValue : 4;

    const { data: events = [] } = useQuery({
        queryKey: ["events", 1, maxEventsCount, i18n.language],
        queryFn: async () => {
            const { rows } = await eventService.getAllEvents(1, maxEventsCount);
            return rows;
        },
    });

    const futureEvents: IEventFlat[] = useMemo(() => {
        if (!events.length) return [];
        const now = new Date();
        return events.filter((event) => {
            if (!event.eventDate) return false;
            const eventDate = new Date(event.eventDate);
            return eventDate >= now;
        });
    }, [events]);

    // Hide whole section if there are no upcoming events
    if (!futureEvents.length) {
        return null;
    }

    return (
        <section className="relative py-20 md:py-24 lg:py-28">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-screen h-full bg-background-400 -z-10" />

            <div className="mx-auto max-w-[360px] md:max-w-[688px] lap:max-w-[1440px] lap:w-full flex flex-col lg:gap-6 lap:flex-row lap:justify-between lap:items-start">
                <div className="flex flex-col max-w-[627px] gap-4">
                    <SectionHeading text={t("home.headings.events")} variant={HeadingVariant.LIGHT} />
                    <p className="w-full md:w-[570px] lap:w-[580px] xl:w-[627px] font-regular text-base text-text-400">
                        {t("home.paragraphs.events")}
                    </p>
                    <div className="mt-[42px]">
                        <EventCardsBlock events={futureEvents} />
                    </div>
                </div>

                <div className="mt-6 lap:mt-0 lap:flex-shrink-0">
                    <Button text={t("buttons.moreEvents")} variant={ButtonVariant.SECONDARY} />
                </div>
            </div>
        </section>
    );
}

